import { type ComponentProps } from 'react';

import { css } from '@compiled/react';
import { useIntl } from 'react-intl';

import Button from '@atlaskit/button-v25/new';
import Heading from '@atlaskit/heading';
import AiChatIcon from '@atlaskit/icon/core/ai-chat';
import ArrowLeftIcon from '@atlaskit/icon/core/arrow-left';
import ArrowRightIcon from '@atlaskit/icon/core/arrow-right';
import AiGenerativeTextIcon from '@atlaskit/icon-lab/core/ai-generative-text';
import { Text } from '@atlaskit/primitives-next';
import { token } from '@atlaskit/tokens';

import { type ArticleAccentColor, type PortalArticle } from '../../jsm-portal-playground-data';
import { jsmPortalPlaygroundMessages as messages } from '../../jsm-portal-playground-messages';

import {
	ARTICLE_DATE_FORMAT,
	BEACON_GRADIENT,
	BRAND_FONT,
	JsmPortalPlaygroundRovoLauncherMark,
	PortalArticleIconTile,
} from './jsm-portal-playground-it-support-shared';

type ButtonIcon = NonNullable<ComponentProps<typeof Button>['iconBefore']>;

const DraftWithRovoIcon: ButtonIcon = () => <AiGenerativeTextIcon label="" size="small" />;
const BackIcon: ButtonIcon = () => <ArrowLeftIcon label="" size="small" />;

export const JsmPortalPlaygroundArticleView = ({
	article,
	secondaryColor,
	onBack,
	onAskAnythingClick,
}: {
	article: PortalArticle;
	secondaryColor: ArticleAccentColor;
	onBack: () => void;
	onAskAnythingClick?: () => void;
}) => {
	const intl = useIntl();

	return (
		<div css={pageStyles} data-testid="jsm-portal-article-page">
			<div css={columnStyles}>
				<div css={backLinkWrapperStyles}>
					<Button
						appearance="subtle"
						spacing="compact"
						iconBefore={BackIcon}
						aria-label={intl.formatMessage(messages.articleBackLabel)}
						onClick={onBack}
						testId="jsm-portal-article-back-button"
					>
						{intl.formatMessage(messages.articleBack)}
					</Button>
				</div>

				<div css={metaRowStyles}>
					<PortalArticleIconTile color={secondaryColor} size="medium" />
					<div css={metaEndStyles}>
						<Text color="color.text.subtlest">
							{intl.formatMessage(messages.lastUpdated, {
								date: intl.formatDate(article.lastUpdated, ARTICLE_DATE_FORMAT),
							})}
						</Text>
						<span
							aria-hidden="true"
							// @ts-expect-error -- inert is missing from @compiled/react types
							inert=""
						>
							<Button iconBefore={DraftWithRovoIcon} spacing="compact" tabIndex={-1}>
								{intl.formatMessage(messages.draftWithRovo)}
							</Button>
						</span>
					</div>
				</div>

				<div css={headerBlockStyles}>
					<h1 css={articleTitleStyles}>{intl.formatMessage(article.title)}</h1>
					<Text size="large">{intl.formatMessage(article.intro)}</Text>
				</div>

				<div css={sectionsStyles}>
					{article.sections.map((section) => (
						<div key={section.heading.id} css={sectionStyles}>
							<Heading size="medium" as="h2">
								{intl.formatMessage(section.heading)}
							</Heading>
							<div css={paragraphGapStyles}>
								{section.paragraphs.map((lines, paragraphIndex) => (
									<div key={paragraphIndex} css={paragraphStyles} aria-hidden="true">
										{lines.map((width, lineIndex) => (
											<div
												key={lineIndex}
												css={redactedLineStyles}
												style={{ width: `${width * 100}%` }}
											/>
										))}
									</div>
								))}
							</div>
						</div>
					))}
				</div>
			</div>

			<div css={stillNeedHelpBandStyles}>
				<div css={stillNeedHelpCardStyles}>
					<div css={stillNeedHelpContentStyles}>
						<div css={stillNeedHelpCopyStyles}>
							<h2 css={stillNeedHelpTitleStyles}>
								{intl.formatMessage(messages.stillNeedHelpTitle)}
							</h2>
							<p css={stillNeedHelpBodyStyles}>{intl.formatMessage(messages.stillNeedHelpBody)}</p>
						</div>

						<button type="button" css={askPillStyles} onClick={onAskAnythingClick}>
							<AiChatIcon label="" color={token('color.text.inverse')} />
							<Text weight="medium" color="color.text.inverse">
								{intl.formatMessage(messages.askAnything)}
							</Text>
							<span css={askPillActionStyles} aria-hidden="true">
								<ArrowRightIcon label="" color={token('color.text')} />
							</span>
						</button>

						<Text weight="medium">{intl.formatMessage(messages.submitRequest)}</Text>
					</div>
				</div>
			</div>

			<JsmPortalPlaygroundRovoLauncherMark />
		</div>
	);
};

const pageStyles = css({
	display: 'flex',
	flexDirection: 'column',
	gap: token('space.100'),
	position: 'relative',
	boxSizing: 'border-box',
	width: '100%',
	height: '100%',
	minHeight: 0,
	paddingBlockStart: token('space.200'),
	paddingBlockEnd: token('space.100'),
	paddingInline: token('space.500'),
	backgroundColor: token('elevation.surface.sunken'),
	overflow: 'hidden',
});

const columnStyles = css({
	display: 'flex',
	flexDirection: 'column',
	alignSelf: 'center',
	gap: token('space.250'),
	boxSizing: 'border-box',
	width: '878px',
	maxWidth: '100%',
});

const backLinkWrapperStyles = css({
	alignSelf: 'flex-start',
});

const metaRowStyles = css({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'space-between',
	gap: token('space.200'),
	width: '100%',
});

const metaEndStyles = css({
	display: 'flex',
	alignItems: 'center',
	flexShrink: 0,
	gap: token('space.075'),
});

const headerBlockStyles = css({
	display: 'flex',
	flexDirection: 'column',
	gap: token('space.150'),
});

const articleTitleStyles = css({
	margin: 0,
	color: token('color.text'),
	fontFamily:
		'"Atlassian Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
	fontSize: '40px',
	fontWeight: 700,
	lineHeight: 1.1,
	letterSpacing: '-0.01em',
});

const sectionsStyles = css({
	display: 'flex',
	flexDirection: 'column',
	gap: token('space.300'),
	width: '100%',
});

const sectionStyles = css({
	display: 'flex',
	flexDirection: 'column',
	gap: token('space.150'),
	width: '100%',
});

const paragraphStyles = css({
	display: 'flex',
	flexDirection: 'column',
	gap: token('space.150'),
	width: '100%',
});

const paragraphGapStyles = css({
	display: 'flex',
	flexDirection: 'column',
	gap: token('space.300'),
	width: '100%',
});

const redactedLineStyles = css({
	height: '12px',
	borderRadius: token('radius.xsmall'),
	backgroundColor: token('color.skeleton'),
});

const stillNeedHelpBandStyles = css({
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'center',
	justifyContent: 'center',
	flexShrink: 0,
	marginBlockStart: 'auto',
	boxSizing: 'border-box',
	width: '100%',
	padding: token('space.150'),
});

const stillNeedHelpCardStyles = css({
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'center',
	boxSizing: 'border-box',
	width: '100%',
	maxWidth: '1202px',
	paddingBlock: token('space.250'),
	paddingInline: token('space.800'),
	borderRadius: token('radius.large'),
	backgroundImage: BEACON_GRADIENT,
	overflow: 'hidden',
});

const stillNeedHelpContentStyles = css({
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'center',
	justifyContent: 'center',
	gap: token('space.200'),
	width: '100%',
	maxWidth: '1081px',
});

const stillNeedHelpCopyStyles = css({
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'center',
	gap: token('space.100'),
});

const stillNeedHelpTitleStyles = css({
	margin: 0,
	color: token('color.text'),
	fontFamily: BRAND_FONT,
	fontSize: '40px',
	fontWeight: 700,
	lineHeight: 1.17,
	textAlign: 'center',
});

const stillNeedHelpBodyStyles = css({
	margin: 0,
	maxWidth: '567px',
	color: token('color.text'),
	fontFamily: BRAND_FONT,
	fontSize: '16px',
	fontWeight: 400,
	lineHeight: 1.25,
	textAlign: 'center',
});

const askPillStyles = css({
	display: 'inline-flex',
	alignItems: 'center',
	gap: token('space.100'),
	height: '60px',
	paddingBlock: token('space.150'),
	paddingInline: token('space.200'),
	borderStyle: 'none',
	borderRadius: token('radius.xxlarge'),
	cursor: 'pointer',
	backgroundColor: token('color.text'),
	transition: 'box-shadow 150ms ease, transform 150ms ease',
	'&:hover': {
		boxShadow: token('elevation.shadow.overlay'),
	},
	'&:active': {
		boxShadow: token('elevation.shadow.raised'),
		transform: 'translateY(1px)',
	},
	'@media (prefers-reduced-motion: reduce)': {
		transition: 'none',
	},
});

const askPillActionStyles = css({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	flexShrink: 0,
	width: '34px',
	height: '34px',
	borderRadius: token('radius.full'),
	// eslint-disable-next-line local/ensure-wac-token-usage
	backgroundColor: '#B2EABB',
});
